import { useEffect } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  MessageCircle,
  User,
  Coins,
  Banknote,
  Building2,
  FileText,
  Send,
  Bell,
  Dice5,
  Calendar,
  Ticket,
  BarChart3,
  Link2,
  Plug,
  Settings,
  LogOut,
  UserPlus,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth.store';
import { useUIStore } from '@/stores/ui.store';
import { OWNER_TENANT_SLUG } from '@/lib/owner';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Solo visible para el dueno de la plataforma (Lucky Soft). */
  ownerOnly?: boolean;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    items: [
      { to: '/chats', label: 'Chats', icon: MessageCircle },
      { to: '/usuarios', label: 'Usuarios', icon: User },
      { to: '/clientes', label: 'Clientes', icon: Coins },
    ],
  },
  {
    title: 'FINANZAS',
    items: [
      { to: '/retiros', label: 'Retiros', icon: Banknote },
      { to: '/cuentas', label: 'Cuentas', icon: Building2 },
      { to: '/auditoria', label: 'Auditoría', icon: FileText },
    ],
  },
  {
    title: 'MARKETING',
    items: [
      { to: '/mensajes', label: 'Mensajes', icon: Send },
      { to: '/notificaciones', label: 'Notificaciones', icon: Bell },
      { to: '/eventos', label: 'Eventos', icon: Dice5 },
      { to: '/bonos', label: 'Bonos', icon: Calendar },
      { to: '/cupones', label: 'Cupones', icon: Ticket },
    ],
  },
  {
    title: 'SISTEMA',
    items: [
      { to: '/invitaciones', label: 'Invitaciones', icon: UserPlus, ownerOnly: true },
      { to: '/metricas', label: 'Métricas', icon: BarChart3 },
      { to: '/apis', label: 'APIs', icon: Link2 },
      { to: '/integraciones', label: 'Integraciones', icon: Plug },
      { to: '/ajustes', label: 'Ajustes', icon: Settings },
    ],
  },
];

export function Sidebar() {
  const { user, logout } = useAuth();
  const { location } = useRouterState();
  const initials = (user?.display_name || user?.username || '??').slice(0, 2).toUpperCase();
  const tenantSlug = useAuthStore((s) => s.tenantSlug);
  const isOwner = tenantSlug === OWNER_TENANT_SLUG;

  // "sidebarCollapsed" doubles as the mobile drawer's closed/open flag.
  // Desktop (md+) always shows the sidebar and ignores this value entirely.
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const setSidebarCollapsed = useUIStore((s) => s.setSidebarCollapsed);
  const isOpen = !sidebarCollapsed;

  // The store persists to localStorage, so a prior session could leave it
  // "open". Force the drawer closed on mount when we're on a mobile
  // viewport so it never covers the screen on first paint. Desktop ignores
  // this state completely, so this has no effect there.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setSidebarCollapsed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeOnMobile = () => setSidebarCollapsed(true);

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-40 flex h-screen w-64 shrink-0 flex-col bg-sidebar text-sidebar-foreground transition-transform duration-300 ease-out',
        isOpen ? 'translate-x-0' : '-translate-x-full',
        'md:static md:z-auto md:translate-x-0',
      )}
    >
      <div className="flex h-16 items-center gap-2 border-b border-sidebar-border px-6">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-sidebar-accent text-base font-bold">
          CC
        </div>
        <div>
          <div className="text-sm font-semibold leading-tight">Clone Chat</div>
          <div className="text-xs text-sidebar-foreground/60">Admin Panel</div>
        </div>
        <button
          type="button"
          onClick={closeOnMobile}
          aria-label="Cerrar menú"
          className="ml-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent md:hidden"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {SECTIONS.map((section, i) => (
          <div key={i} className={cn(i > 0 && 'mt-6')}>
            {section.title && (
              <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/50">
                {section.title}
              </div>
            )}
            <ul className="space-y-1">
              {section.items.filter((item) => !item.ownerOnly || isOwner).map((item) => {
                const isActive = location.pathname.startsWith(item.to);
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onClick={closeOnMobile}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-sidebar-border p-3">
        <div className="flex items-center gap-3 rounded-md px-2 py-2">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-xs">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{user?.display_name || user?.username || 'Invitado'}</div>
            <div className="truncate text-xs text-sidebar-foreground/60">{user?.role || '—'}</div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
            onClick={() => void logout()}
            title="Salir"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </aside>
  );
}
